import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import { usePreserveQueryParams } from '../hooks/usePreserveQueryParams'
import CategoryDiscovery from '../components/CategoryDiscovery'
import QueryObjectives from '../components/QueryObjectives'
import QueryObjectivesPieChart from '../components/QueryObjectivesPieChart'
import CitationsCard from '../components/CitationsCard'
import AIMentionsCard from '../components/AIMentionsCard'
import GoogleRankingsCard from '../components/GoogleRankingsCard'
import AuthoritySources from '../components/AuthoritySources'
import SearchIntelligenceInsights from '../components/SearchIntelligenceInsights'
import { demoVisibilityData, demoIntelligenceOverview, DEMO_BRAND_NAME } from '../utils/demoData'
import {
  Target,
  AlertCircle,
  BarChart3,
  Search,
  ArrowRight,
  Flame,
  Award,
  RefreshCw,
  Bot,
  Sparkles,
  Quote,
} from 'lucide-react'

interface IntelligenceOverview {
  totalQueries: number
  classifiedQueries: number
  queriesUserMentioned: number
  queriesCompetitorMentioned: number
  contentGapQueries: number
  topicsCovered: number
  userMentionRate: string
  competitorMentionRate: string
  intentBreakdown: Array<{
    intentType: string
    description?: string
    queryCount: number
    percentage: string
    avgCommercialIntent: string
  }>
  contentOpportunities: Array<{
    id: string
    query: string
    topic: {
      name: string
      slug: string
      icon: string
    }
    commercialIntent: number
    intentType: string
    competitorCount: number
  }>
}

interface VisibilityData {
  current: {
    visibilityScore: number | null
    aiMentionScore: number
    searchRankingScore: number | null
    citationAuthorityScore: number | null
  }
  details: {
    aiMentions: {
      score: number
      totalQueries: number
      queriesMentioned: number
      competitorQueriesMentioned: number
      mentionRate: number
      competitorMentionRate: number
      avgPosition: number | null
      competitorAvgPosition: number | null
      queries?: Array<{
        id: string
        queryText: string
        createdAt: string
        brandsMentioned: Array<{
          brandId: string
          brandName: string
          isUserBrand: boolean
          position: number | null
        }>
      }>
    }
    searchRankings: {
      score: number
      avgPosition: number
      searchFrequency: number
      featuredSnippets: number
      knowledgePanel: boolean
      totalQueriesAnalyzed: number
      lastAnalyzed: string
      scoreBreakdown?: {
        contextBreakdown?: Record<string, {
          mentionRate: number
          mentioned: number
          total: number
        }>
      }
    } | null
    citations: {
      score: number
      totalCitations: number
      authoritative: number
      newsMentions: number
      topSources: Array<{ domain: string; count: number; sourceType: string }>
      sampleCitations?: Array<{
        excerpt: string
        source: string
        mentionedBrand: string
        context?: string
      }>
      trendingTopics?: Array<{ topic: string; count: number }>
      viralMoments?: number
      sourceDiversityScore?: number
      lastAnalyzed: string
      scoreBreakdown?: {
        contextBreakdown?: Record<string, {
          citationCount: number
          percentage: number
        }>
      }
    } | null
  }
}

export default function SearchIntelligence() {
  const { activeBrand } = useAuth()
  const { withParams } = usePreserveQueryParams()
  const [overview, setOverview] = useState<IntelligenceOverview | null>(null)
  const [visibilityData, setVisibilityData] = useState<VisibilityData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [processingStatus, setProcessingStatus] = useState<'checking' | 'processing' | 'completed' | 'failed'>('checking')
  const [triggeringAnalysis, setTriggeringAnalysis] = useState(false)

  // Check for demo mode via URL parameter
  const urlParams = new URLSearchParams(window.location.search)
  const isDemoMode = urlParams.get('demo') === 'true'

  // Function to trigger intelligence processing
  const triggerIntelligenceProcessing = async () => {
    if (!activeBrand?.userBrandId) return

    try {
      setTriggeringAnalysis(true)
      await api.post('/query-intelligence/trigger', {
        userBrandId: activeBrand.userBrandId
      })

      // Refresh the page to show processing status
      window.location.reload()
    } catch (error: any) {
      console.error('Failed to trigger intelligence processing:', error)
      alert(error.response?.data?.error || 'Failed to trigger analysis. Please try again.')
    } finally {
      setTriggeringAnalysis(false)
    }
  }

  useEffect(() => {
    if (!activeBrand?.userBrandId && !isDemoMode) return

    const initializeIntelligence = async () => {
      try {
        setLoading(true)
        setError(null)

        // In demo mode, just load demo data and return
        if (isDemoMode) {
          setVisibilityData(demoVisibilityData)
          setOverview(demoIntelligenceOverview as any)
          setProcessingStatus('completed')
          setLoading(false)
          return
        }

        // TypeScript: activeBrand exists here because we checked earlier and returned if not
        if (!activeBrand) return

        // Check processing status (read-only, no triggering)
        try {
          const statusResponse = await api.get(`/query-intelligence/status/${activeBrand.userBrandId}`)
          if (statusResponse.data.status === 'processing') {
            setProcessingStatus('processing')
            pollProcessingStatus()
          } else {
            setProcessingStatus('completed')
          }
        } catch (statusError) {
          // If no status exists, that's fine - user hasn't triggered processing yet
          setProcessingStatus('completed')
        }

        // Fetch overview data (read-only)
        const response = await api.get(`/intelligence/overview?userBrandId=${activeBrand.userBrandId}`)

        // Fetch visibility data
        if (false) {
          setVisibilityData(demoVisibilityData)
        } else {
          try {
            const visibilityResponse = await api.get(`/visibility/overview/${activeBrand.id}`)
            const actualData = visibilityResponse.data.data || visibilityResponse.data
            setVisibilityData(actualData)
          } catch (visErr) {
            console.error('Error fetching visibility data:', visErr)
            // Don't fail the whole page if visibility data fails
          }
        }

        // Use real data from API if not in demo mode
        if (!isDemoMode) {
          setOverview(response.data)
        }
      } catch (err: any) {
        console.error('Error fetching intelligence overview:', err)
        setError(err.response?.data?.error || 'Failed to load intelligence data')
        setProcessingStatus('failed')
      } finally {
        setLoading(false)
      }
    }

    const pollProcessingStatus = async () => {
      const checkStatus = async () => {
        if (!activeBrand) return

        try {
          const statusResponse = await api.get(`/query-intelligence/status/${activeBrand.userBrandId}`)
          const status = statusResponse.data.status

          if (status === 'completed') {
            setProcessingStatus('completed')
            // Refresh overview data
            const response = await api.get(`/intelligence/overview?userBrandId=${activeBrand.userBrandId}`)

            // Only use demo data if demo mode is enabled
            if (isDemoMode) {
              // Demo data will already be set from initial load
            } else {
              setOverview(response.data)
            }
          } else if (status === 'failed') {
            setProcessingStatus('failed')
          } else if (status === 'processing') {
            // Check again in 5 seconds
            setTimeout(checkStatus, 5000)
          }
        } catch (err) {
          console.error('Error checking processing status:', err)
        }
      }

      checkStatus()
    }

    initializeIntelligence()
  }, [activeBrand?.userBrandId, isDemoMode])

  if (!activeBrand && !isDemoMode) {
    return (
      <div className="space-y-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
            <p className="text-yellow-800">Please select a brand to view intelligence insights.</p>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!overview) return null

  return (
      <div className="space-y-6">

        {/* Gradient Header Banner */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Left: Title & Description */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Bot className="w-5 h-5 text-indigo-200" />
                <span className="text-indigo-200 text-sm font-medium uppercase tracking-wide">AI Visibility Analysis</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2">
                {isDemoMode ? DEMO_BRAND_NAME : (activeBrand?.companyName || 'Your Brand')}<span className="text-indigo-200"> in Search</span>
              </h2>
              <p className="text-indigo-100 text-sm md:text-base">
                Discover where AI assistants mention you, what sources they cite, and where you can improve visibility
              </p>
            </div>

            {/* Right: Stats Badges - Grid on mobile, flex on desktop */}
            <div className="grid grid-cols-3 md:flex md:flex-wrap gap-2 md:gap-3 w-full md:w-auto">
              {/* AI Mention Rate */}
              <div className="bg-white/20 rounded-lg px-3 md:px-4 py-2 md:py-3 text-center flex-1 md:flex-none md:min-w-[100px]">
                <div className="flex items-center justify-center gap-1 md:gap-1.5 mb-1">
                  <Bot className="w-3 h-3 md:w-4 md:h-4" />
                  <span className="text-lg md:text-2xl font-bold">
                    {visibilityData?.details?.aiMentions?.mentionRate != null
                      ? `${Math.round(visibilityData.details.aiMentions.mentionRate)}%`
                      : '--'}
                  </span>
                </div>
                <p className="text-xs text-indigo-200">AI Mention</p>
              </div>
              {/* Google Rank */}
              <div className="bg-white/20 rounded-lg px-3 md:px-4 py-2 md:py-3 text-center flex-1 md:flex-none md:min-w-[100px]">
                <div className="flex items-center justify-center gap-1 md:gap-1.5 mb-1">
                  <Search className="w-3 h-3 md:w-4 md:h-4" />
                  <span className="text-lg md:text-2xl font-bold">
                    {visibilityData?.details?.searchRankings?.avgPosition != null
                      ? `#${Math.round(visibilityData.details.searchRankings.avgPosition)}`
                      : '--'}
                  </span>
                </div>
                <p className="text-xs text-indigo-200">Google Rank</p>
              </div>
              {/* Sources */}
              <div className="bg-white/20 rounded-lg px-3 md:px-4 py-2 md:py-3 text-center flex-1 md:flex-none md:min-w-[100px]">
                <div className="flex items-center justify-center gap-1 md:gap-1.5 mb-1">
                  <Quote className="w-3 h-3 md:w-4 md:h-4" />
                  <span className="text-lg md:text-2xl font-bold">
                    {visibilityData?.details?.citations?.totalCitations ?? '--'}
                  </span>
                </div>
                <p className="text-xs text-indigo-200">Sources</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-4 pt-4 border-t border-white/20 flex flex-wrap gap-3">
            {/* Show Discover Categories button if no queries exist */}
            {overview.totalQueries === 0 && (
              <button
                onClick={triggerIntelligenceProcessing}
                disabled={triggeringAnalysis || processingStatus === 'processing'}
                className="inline-flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {triggeringAnalysis ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin"/>
                    Starting Analysis...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2"/>
                    Discover Categories
                  </>
                )}
              </button>
            )}
            <Link
              to={withParams('/search-queries')}
              className="inline-flex items-center px-4 py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 font-medium transition-colors"
            >
              <Search className="w-4 h-4 mr-2"/>
              View All Queries
              <ArrowRight className="w-4 h-4 ml-2"/>
            </Link>
          </div>
        </div>


        {/* Processing Status Banner */}
        {processingStatus === 'processing' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center">
                <RefreshCw className="w-5 h-5 text-blue-600 mr-3 animate-spin"/>
                <div>
                  <p className="text-blue-900 font-medium">Processing Intelligence Data</p>
                  <p className="text-blue-700 text-sm">
                    Discovering brand categories and classifying queries... This may take a minute.
                  </p>
                </div>
              </div>
            </div>
        )}

        {/* Search Intelligence Insights - AI-powered strategies */}
        {processingStatus !== 'processing' && activeBrand && (
            <SearchIntelligenceInsights />
        )}

        {/* Brand Visibility */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          {/* AI Mention Rate */}
          <AIMentionsCard mentions={visibilityData?.details?.aiMentions || null} />

          {/* Search Rankings */}
          <GoogleRankingsCard
              rankings={visibilityData?.details?.searchRankings || null}
              brandName={activeBrand?.companyName || ''}
              brandId={activeBrand?.id || ''}
              onRefresh={() => {
                if (activeBrand?.id) {
                  api.get(`/visibility/overview/${activeBrand.id}`)
                      .then(res => setVisibilityData(res.data.data))
                      .catch(console.error)
                }
              }}
          />

          {/* PR/News Authority */}
          <CitationsCard
              citations={visibilityData?.details?.citations || null}
              brandId={activeBrand?.id || ''}
              onRefresh={() => {
                // Refresh visibility data after citations are updated
                if (activeBrand?.id) {
                  api.get(`/visibility/overview/${activeBrand.id}`)
                      .then(res => setVisibilityData(res.data.data))
                      .catch(console.error)
                }
              }}
          />
        </div>

        {/* Authority Sources */}
        {processingStatus !== 'processing' && (activeBrand || isDemoMode) && (
            <AuthoritySources />
        )}

        {/* Brand-Specific Categories - Market segments unique to your brand */}
        {processingStatus !== 'processing' && (activeBrand || isDemoMode) && (
            <CategoryDiscovery
                userBrandId={activeBrand?.userBrandId || 'demo'}
            />
        )}

        {/* User Purchase Phase */}
        <div className="p-6 bg-white rounded-lg shadow space-y-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Target className="w-6 h-6 text-indigo-600" />
              <h2 className="text-xl font-bold text-gray-900">AI Search Insights</h2>
            </div>
            <p className="text-sm text-gray-600">What are users trying to accomplish when searching AI assistants?</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="md:sticky md:top-6 md:self-start">
              {/* Query Objectives Pie Chart */}
              {processingStatus !== 'processing' && (
                  <QueryObjectivesPieChart userBrandId={activeBrand?.userBrandId || 'demo'} demoMode={isDemoMode}/>
              )}
            </div>
            <div>

              {/* Query Objectives Cards List */}
              {processingStatus !== 'processing' && (
                <QueryObjectives userBrandId={activeBrand?.userBrandId || 'demo'} demoMode={isDemoMode}/>
              )}
            </div>
          </div>

        </div>

        {/* Content Opportunities */}
        {overview.contentOpportunities.length > 0 && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                      <Flame className="w-5 h-5 text-orange-500 mr-2"/>
                      Top Content Opportunities
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">High-value queries where competitors appear but you
                      don't</p>
                  </div>
                  <span className="text-2xl font-bold text-orange-600">{overview.contentOpportunities.length}</span>
                </div>
              </div>
              <div className="divide-y divide-gray-200">
                {overview.contentOpportunities.map((opp, idx) => (
                    <div key={opp.id} className="p-6 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                      <span
                          className="flex items-center justify-center w-6 h-6 rounded-full bg-orange-100 text-orange-700 text-xs font-bold">
                        #{idx + 1}
                      </span>
                            {opp.topic?.icon && <span className="text-lg">{opp.topic.icon}</span>}
                            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                        {opp.topic?.name || 'Other'}
                      </span>
                            <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                        {opp.intentType}
                      </span>
                          </div>
                          <p className="text-gray-900 font-medium mb-2">{opp.query}</p>
                          <div className="flex items-center space-x-4 text-xs text-gray-600">
                            <div className="flex items-center">
                              <Target className="w-3 h-3 mr-1 text-orange-500"/>
                              <span>Intent: {opp.commercialIntent}/100</span>
                            </div>
                            <div className="flex items-center">
                              <BarChart3 className="w-3 h-3 mr-1 text-purple-500"/>
                              <span>{opp.competitorCount} competitors mentioned</span>
                            </div>
                          </div>
                        </div>
                        <div className="ml-4">
                          {opp.commercialIntent >= 60 && (
                              <span
                                  className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-700">
                        <Award className="w-3 h-3 mr-1"/>
                        High Value
                      </span>
                          )}
                        </div>
                      </div>
                    </div>
                ))}
              </div>
            </div>
        )}

      </div>
  )
}
